import logoImage from "@/assets/werbekreis-logo.png";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Lightbulb, Calendar, CreditCard, Users, Menu, X, Mail, LogIn, LogOut, User } from "@/components/icons";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
const Layout = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const {
    toast
  } = useToast();
  const {
    user,
    signOut
  } = useAuth();

  // Newsletter state
  const [newsletterData, setNewsletterData] = useState({
    name: "",
    email: ""
  });
  const [isSubmittingNewsletter, setIsSubmittingNewsletter] = useState(false);
  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterData.name || !newsletterData.email) {
      toast({
        title: "Fehler",
        description: "Bitte geben Sie Name und E-Mail-Adresse ein.",
        variant: "destructive"
      });
      return;
    }
    setIsSubmittingNewsletter(true);
    try {
      const {
        error
      } = await supabase.from("newsletter_subscriptions").insert([{
        name: newsletterData.name,
        email: newsletterData.email
      }]);
      if (error) {
        if (error.code === '23505') {
          // Unique constraint violation (duplicate email)
          toast({
            title: "Bereits angemeldet",
            description: "Diese E-Mail-Adresse ist bereits für unseren Newsletter angemeldet.",
            variant: "destructive"
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: "Newsletter abonniert!",
          description: "Vielen Dank! Sie erhalten ab sofort unsere Newsletter."
        });
        setNewsletterData({
          name: "",
          email: ""
        });
      }
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      toast({
        title: "Fehler",
        description: "Newsletter-Anmeldung fehlgeschlagen. Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
    } finally {
      setIsSubmittingNewsletter(false);
    }
  };
  const navigation = [{
    name: "Home",
    href: "/",
    icon: null
  }, {
    name: "Idee einreichen",
    href: "/ideen",
    icon: Lightbulb
  }, {
    name: "Events",
    href: "/events",
    icon: Calendar
  }, {
    name: "Gutscheine",
    href: "/gutscheine",
    icon: CreditCard
  }, {
    name: "Mitmachen",
    href: "/mitmachen",
    icon: Users
  }];
  const isActiveRoute = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };
  return <div className="min-h-screen bg-gradient-to-br from-background via-secondary/30 to-accent/20">
      {/* Smart City Header */}
      <header className={`sticky top-0 z-50 border-b transition-all duration-300 ${isScrolled ? "border-border bg-background/95 backdrop-blur-lg shadow-sm" : "border-border/50 bg-background/80 backdrop-blur-md"}`}>
        <div className="container mx-auto px-4">
          <div className={`flex items-center justify-between transition-all duration-300 ${isScrolled ? "h-14" : "h-16"}`}>
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <img src={logoImage} alt="Werbekreis Roding 1979 e.V." width={600} height={227} className={`w-auto transition-all duration-300 ${isScrolled ? "h-9" : "h-12"}`} />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <nav className="flex items-center space-x-1">
                {navigation.map(item => {
                const Icon = item.icon;
                return <Link key={item.name} to={item.href} className={`nav-link px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${isActiveRoute(item.href) ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent/50"}`}>
                      <div className="flex items-center space-x-2">
                        {Icon && <Icon className="h-4 w-4" />}
                        <span>{item.name}</span>
                      </div>
                    </Link>;
              })}
              </nav>
              
              {/* Admin/Login Button */}
              {user ? <Button variant="default" size="sm" asChild>
                  <Link to="/admin">
                    <User className="h-4 w-4 mr-2" />
                    Admin
                  </Link>
                </Button> : <Button variant="default" size="sm" asChild>
                  <Link to="/auth/login">
                    <LogIn className="h-4 w-4 mr-2" />
                    Login
                  </Link>
                </Button>}
            </div>

            {/* Mobile Menu Button */}
            <Button variant="ghost" size="icon" className="md:hidden" aria-label={isMobileMenuOpen ? "Menü schließen" : "Menü öffnen"} aria-expanded={isMobileMenuOpen} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && <nav className="md:hidden py-4 space-y-2 animate-slide-up">
              {navigation.map(item => {
            const Icon = item.icon;
            return <Link key={item.name} to={item.href} className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${isActiveRoute(item.href) ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent/50"}`} onClick={() => setIsMobileMenuOpen(false)}>
                    <div className="flex items-center space-x-3">
                      {Icon && <Icon className="h-5 w-5" />}
                      <span>{item.name}</span>
                    </div>
                  </Link>;
          })}
              
              {/* Mobile Admin/Login Button */}
              <div className="px-4 pt-2">
                {user ? <Button variant="default" size="sm" asChild className="w-full">
                    <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)}>
                      <User className="h-4 w-4 mr-2" />
                      Admin
                    </Link>
                  </Button> : <Button variant="default" size="sm" asChild className="w-full">
                    <Link to="/auth/login" onClick={() => setIsMobileMenuOpen(false)}>
                      <LogIn className="h-4 w-4 mr-2" />
                      Login
                    </Link>
                  </Button>}
              </div>
            </nav>}
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {children}
      </main>

      {/* Smart Footer */}
      <footer className="border-t border-border/50 bg-card/50 backdrop-blur-sm mt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Organization Info */}
            <div className="space-y-3">
              <Link to="/" className="inline-block">
                <img src={logoImage} alt="Werbekreis Roding 1979 e.V." width={600} height={227} className="h-12 w-auto" />
              </Link>
              <h3 className="font-semibold text-lg gradient-text">Werbekreis Roding 1979 e.V.</h3>
              <p className="text-sm text-muted-foreground">
                Gemeinsam für ein lebendiges Roding – Ihre lokale Gemeinschaft für Handel, 
                Events und Bürgerbeteiligung.
              </p>
            </div>

            {/* Quick Links */}
            <div className="space-y-3">
              <h4 className="font-medium text-foreground">Schnellzugriff</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/ideen" className="text-muted-foreground hover:text-primary transition-colors">Idee einreichen</Link></li>
                <li><Link to="/events" className="text-muted-foreground hover:text-primary transition-colors">Aktuelle Events</Link></li>
                <li><Link to="/mitmachen" className="text-muted-foreground hover:text-primary transition-colors">Mitglied werden</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div className="space-y-3">
              <h4 className="font-medium text-foreground">Kontakt</h4>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Werbekreis Roding 1979 e.V.</p>
                <p>Schulstr. 16</p>
                <p>93426 Roding</p>
                <p>vorstand@werbekreis-roding.de</p>
              </div>
            </div>

            {/* Legal */}
            <div className="space-y-3">
              <h4 className="font-medium text-foreground">Rechtliches</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/impressum" className="text-muted-foreground hover:text-primary transition-colors">Impressum</Link></li>
                <li><Link to="/datenschutz" className="text-muted-foreground hover:text-primary transition-colors">Datenschutz</Link></li>
                <li><Link to="/agb-gutscheine" className="text-muted-foreground hover:text-primary transition-colors">AGB Gutscheinkarte</Link></li>
                <li><Link to="/dokumente" className="text-muted-foreground hover:text-primary transition-colors">Dokumente</Link></li>
                <li><a href="/Satzung.pdf" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">Satzung (PDF)</a></li>
              </ul>
            </div>
          </div>

          {/* Newsletter */}
          <div className="mt-8 pt-8 border-t border-border/50">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-6 lg:space-y-0 lg:space-x-8">
              <div>
                <h4 className="font-medium text-foreground flex items-center">
                  <Mail className="h-5 w-5 mr-2 text-primary" />
                  Newsletter abonnieren
                </h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Bleiben Sie über Events und Neuigkeiten informiert
                </p>
              </div>
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                <Input type="text" placeholder="Ihr Name" value={newsletterData.name} onChange={e => setNewsletterData({
                ...newsletterData,
                name: e.target.value
              })} className="w-full sm:w-48" required />
                <Input type="email" placeholder="Ihre E-Mail-Adresse" value={newsletterData.email} onChange={e => setNewsletterData({
                ...newsletterData,
                email: e.target.value
              })} className="w-full sm:w-64" required />
                <Button type="submit" size="sm" className="btn-werbekreis whitespace-nowrap" disabled={isSubmittingNewsletter}>
                  {isSubmittingNewsletter ? "Anmelden..." : "Anmelden"}
                </Button>
              </form>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-6 pt-6 border-t border-border/50 text-center text-sm text-muted-foreground">
            <p>© 2026 Werbekreis Roding 1979 e.V. - Alle Rechte vorbehalten</p>
          </div>
        </div>
      </footer>
    </div>;
};
export default Layout;